import { jsPDF } from 'jspdf';
import { API_BASE } from '../api';
import { showToast } from '../components/toast';
import type { CatalogueItem } from '../util/ndis-catalogue';
import { groupCatalogueByCategory, parseCatalogueRate, readCatalogueFromStorage, searchCatalogueItems } from '../util/ndis-catalogue-search';

interface ParticipantBudgetItem {
  category: string;
  amount: number;
  managementType: string;
  sourceSnippet: string;
}

interface ParticipantPlanContext {
  planStartDate: string;
  planEndDate: string;
  planManagementType: 'self' | 'plan-managed' | 'agency' | 'unknown';
  budgets: ParticipantBudgetItem[];
  participantNameFromPlan: string;
  ndisNumberFromPlan: string;
  warnings: string[];
  sourceType: 'pdf-upload' | 'pasted-text' | 'manual';
  sourceFileName: string;
  extractedAt: string;
}

interface ParticipantApplicableCode {
  supportItemCode: string;
  supportItemName: string;
  category: string;
  registrationGroup: string;
  unit: string;
  claimType: string;
  priceLimit: number;
  source: 'catalogue-matched' | 'manual';
  notes: string;
  reviewStatus: 'needs-review' | 'approved' | 'inactive';
  selectedAt: string;
}

interface ParticipantProfileDraft {
  name: string;
  ndisNumber: string;
  billingType: 'plan-managed' | 'self-managed' | 'ndia-managed' | 'unknown';
  planManagerName: string;
  planManagerEmail: string;
  phone: string;
  address: string;
  invoiceNotes: string;
  preferredInvoiceRecipient: string;
  planContext?: ParticipantPlanContext;
  applicableCodes: ParticipantApplicableCode[];
}

interface ProviderProfileDraft {
  providerType: 'individual' | 'sole-trader-trading' | 'company' | 'organisation';
  businessName: string;
  supportWorkerName: string;
  abn: string;
  invoiceEmail: string;
  phone: string;
  address: string;
  serviceTypes: string;
  defaultHourlyRate: string;
  defaultKmRate: string;
  gstRegistered: 'yes' | 'no' | 'unknown';
  paymentTerms: string;
  invoiceFooter: string;
  paymentAccountName: string;
  paymentBSB: string;
  paymentAccountNumber: string;
  paymentReferencePreference: 'invoice-number' | 'participant-name' | 'custom';
  paymentReferenceCustom: string;
  paymentNotes: string;
  paymentDetailsPlaceholder: string;
}

interface BillingIdentitySnapshot {
  providerType: string;
  legalName: string;
  tradingName: string;
  businessName: string;
  abn: string;
  invoiceEmail: string;
  phone: string;
  address: string;
  gstRegistered: string;
  paymentAccountName: string;
  paymentBSB: string;
  paymentAccountNumber: string;
  paymentReferencePreference: string;
  paymentReferenceCustom: string;
  paymentNotes: string;
  snapshotAt: string;
}

interface SupportSessionDraft {
  participantName: string;
  providerName: string;
  serviceDate: string;
  startTime: string;
  endTime: string;
  breakMinutes: number;
  calculatedDuration: number;
  serviceMode: 'in-person' | 'phone' | 'video' | 'community-access' | 'other';
  location: string;
  kmTravelled: number;
  kmRate: number;
  supportNotes: string;
}

interface CatalogueReference {
  supportItemNumber: string;
  supportItemName: string;
  supportCategory: string;
  registrationGroup: string;
  unit: string;
  priceLimit: string;
  nonFaceToFace: boolean;
  providerTravel: boolean;
  shortNoticeCancellation: boolean;
}

interface InvoiceLineItem {
  id: string;
  supportItemCode: string;
  supportItemName: string;
  date: string;
  startTime: string;
  endTime: string;
  duration: number;
  unit: 'Hour' | 'Each' | 'Km' | 'Day';
  quantity: number;
  rate: number;
  gstComponent: number;
  claimType: string;
  reviewStatus: 'draft' | 'reviewed' | 'needs-review';
  source: 'participant-approved' | 'catalogue-matched' | 'manual-needs-review';
  catalogueRef?: CatalogueReference;
}

interface InvoiceDraft {
  invoiceNumber: string;
  invoiceDate: string;
  serviceDate: string;
  dueDate: string;
  referenceNote: string;
  status: 'Draft';
  participant: ParticipantProfileDraft;
  provider: ProviderProfileDraft;
  session: SupportSessionDraft;
  lineItems: InvoiceLineItem[];
  billingSnapshot?: BillingIdentitySnapshot;
}

interface InvoiceValidationIssue {
  field: string;
  message: string;
  severity: 'error' | 'warning';
}

type InvoiceTab = 'session' | 'details' | 'lines' | 'validation' | 'preview';
type CatalogueModalMode = 'search' | 'browse';

const INVOICE_STORAGE_KEY = 'hub-invoice-draft';
const INVOICE_REVIEW_STORAGE_KEY = 'hub-invoice-review-draft';
const PARTICIPANT_STORAGE_KEY = 'hub-participant-draft';
const PROVIDER_STORAGE_KEY = 'hub-provider-draft';
const CATALOGUE_STORAGE_KEY = 'hub-ndis-catalogue';
const ACCOUNT_EMAIL = 'accounts@the-hub-ecosystems.com';
const GENERATED_FOOTER_TEXT = 'Generated By; The Hub Ecosystems';

let invoiceDraft: InvoiceDraft = defaultInvoiceDraft();
let activeTab: InvoiceTab = 'session';
let catalogueModalOpen = false;
let catalogueSearch = '';
let catalogueModalMode: CatalogueModalMode = 'search';
let expandedCatalogueCategories = new Set<string>();
let targetLineId: string | null = null;
let emailResult: { status: 'success' | 'error'; message: string; timestamp: string } | null = null;
let sendingEmail = false;
let sendingReviewEmail = false;

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function defaultParticipant(): ParticipantProfileDraft {
  return {
    name: '',
    ndisNumber: '',
    billingType: 'unknown',
    planManagerName: '',
    planManagerEmail: '',
    phone: '',
    address: '',
    invoiceNotes: '',
    preferredInvoiceRecipient: '',
    applicableCodes: [],
  };
}

function defaultProvider(): ProviderProfileDraft {
  return {
    providerType: 'individual',
    businessName: '',
    supportWorkerName: '',
    abn: '',
    invoiceEmail: '',
    phone: '',
    address: '',
    serviceTypes: '',
    defaultHourlyRate: '',
    defaultKmRate: '',
    gstRegistered: 'unknown',
    paymentTerms: '',
    invoiceFooter: '',
    paymentAccountName: '',
    paymentBSB: '',
    paymentAccountNumber: '',
    paymentReferencePreference: 'invoice-number',
    paymentReferenceCustom: '',
    paymentNotes: '',
    paymentDetailsPlaceholder: '',
  };
}

function defaultSession(): SupportSessionDraft {
  const day = today();
  return {
    participantName: '',
    providerName: '',
    serviceDate: day,
    startTime: '',
    endTime: '',
    breakMinutes: 0,
    calculatedDuration: 0,
    serviceMode: 'in-person',
    location: '',
    kmTravelled: 0,
    kmRate: 0,
    supportNotes: '',
  };
}

function defaultLineItem(): InvoiceLineItem {
  const day = invoiceDraft?.session?.serviceDate || today();
  const duration = invoiceDraft?.session ? calculateDuration(invoiceDraft.session) : 0;
  return {
    id: crypto.randomUUID(),
    supportItemCode: '',
    supportItemName: '',
    date: day,
    startTime: invoiceDraft?.session?.startTime || '',
    endTime: invoiceDraft?.session?.endTime || '',
    duration,
    unit: 'Hour',
    quantity: duration || 1,
    rate: 0,
    gstComponent: 0,
    claimType: '',
    reviewStatus: 'draft',
    source: 'manual-needs-review',
  };
}

function defaultInvoiceDraft(): InvoiceDraft {
  const day = today();
  const session = defaultSession();
  return {
    invoiceNumber: '',
    invoiceDate: day,
    serviceDate: day,
    dueDate: '',
    referenceNote: '',
    status: 'Draft',
    participant: defaultParticipant(),
    provider: defaultProvider(),
    session,
    lineItems: [],
  };
}

function injectStyles(): void {
  if (document.getElementById('nid-generator-styles')) return;
  const style = document.createElement('style');
  style.id = 'nid-generator-styles';
  style.textContent = `
body.sp-public-body {
  display: block !important;
  min-height: 100dvh;
  height: auto !important;
  overflow-y: auto !important;
  background: var(--bg) !important;
}
.sp-public-root {
  display: block !important;
  width: 100% !important;
  min-height: 100dvh !important;
  height: auto !important;
  overflow: visible !important;
  flex: none !important;
  background: var(--bg);
}
.nid-page { min-height: 100dvh; width: 100%; box-sizing: border-box; background: var(--bg); color: var(--fg); font-family: var(--font-sans); padding: 24px; }
.nid-shell { width: 100%; max-width: 1240px; margin: 0 auto; }
.nid-topbar { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 18px; padding-bottom: 16px; border-bottom: 1px solid var(--border); }
.nid-brand { color: var(--fg-muted); font-size: 12px; font-weight: 700; text-transform: uppercase; }
.nid-header h1 { margin: 6px 0 6px; color: var(--fg); font-size: 24px; line-height: 1.2; font-weight: 800; }
.nid-header p { margin: 0; color: var(--fg-muted); font-size: 14px; line-height: 1.45; }
.nid-badges { display: flex; justify-content: flex-end; gap: 8px; flex-wrap: wrap; }
.nid-badge { display: inline-flex; align-items: center; min-height: 24px; padding: 3px 9px; border-radius: 4px; border: 1px solid var(--border); background: var(--bg-secondary); color: var(--fg-muted); font-size: 11px; font-weight: 700; text-transform: uppercase; }
.nid-badge-warning { border-color: var(--warning); color: var(--warning); }
.nid-warning { margin-bottom: 16px; padding: 12px 14px; border: 1px solid var(--warning); border-radius: 8px; background: var(--bg-secondary); color: var(--fg); font-size: 13px; line-height: 1.45; font-weight: 700; }
.nid-tabs { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 14px; }
.nid-tab { min-height: 36px; border: 1px solid var(--border); border-radius: 6px; background: var(--bg-secondary); color: var(--fg-muted); padding: 7px 10px; font: inherit; font-size: 12px; font-weight: 800; cursor: pointer; }
.nid-tab-active { border-color: var(--accent); color: var(--fg); background: var(--bg-tertiary); }
.nid-card { border: 1px solid var(--border); border-radius: 8px; background: var(--bg-secondary); padding: 14px; margin-bottom: 14px; }
.nid-card h2 { margin: 0 0 12px; color: var(--fg); font-size: 17px; line-height: 1.25; }
.nid-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.nid-field { min-width: 0; }
.nid-field-wide { grid-column: 1 / -1; }
.nid-label { display: block; margin-bottom: 5px; color: var(--fg-muted); font-size: 12px; font-weight: 700; }
.nid-input, .nid-select, .nid-textarea { width: 100%; min-height: 38px; box-sizing: border-box; border: 1px solid var(--border); border-radius: 6px; background: var(--bg); color: var(--fg); padding: 8px 10px; font: inherit; font-size: 13px; }
.nid-textarea { min-height: 86px; resize: vertical; }
.nid-readonly { min-height: 38px; box-sizing: border-box; border: 1px solid var(--border); border-radius: 6px; background: var(--bg-tertiary); color: var(--fg); padding: 9px 10px; font-size: 13px; font-weight: 700; }
.nid-help { margin-top: 6px; color: var(--fg-muted); font-size: 12px; line-height: 1.45; }
.nid-error-text { margin-top: 6px; color: var(--danger); font-size: 12px; font-weight: 800; }
.nid-actions { display: flex; gap: 10px; margin-top: 12px; flex-wrap: wrap; }
.nid-button { min-height: 38px; border: 1px solid var(--accent); border-radius: 6px; background: var(--accent); color: var(--bg); padding: 8px 14px; font: inherit; font-size: 13px; font-weight: 800; cursor: pointer; }
.nid-button-secondary { border-color: var(--border); background: var(--bg); color: var(--fg); }
.nid-button-danger { border-color: var(--danger); background: var(--bg); color: var(--danger); }
.nid-button:disabled { border-color: var(--border); background: var(--bg-tertiary); color: var(--fg-muted); cursor: not-allowed; }
.nid-participant-code-panel { border: 1px solid var(--border); border-radius: 8px; background: var(--bg); padding: 12px; margin-bottom: 12px; }
.nid-participant-code-panel h3 { margin: 0 0 8px; color: var(--fg); font-size: 15px; }
.nid-line-list { display: grid; gap: 12px; margin-top: 12px; }
.nid-line-card { border: 1px solid var(--border); border-radius: 8px; background: var(--surface-1); padding: 12px; box-shadow: var(--shadow-sm); }
.nid-line-review { margin: -4px 0 10px; padding: 8px 10px; border: 1px solid var(--warning); border-radius: 6px; background: var(--bg-secondary); color: var(--warning); font-size: 12px; font-weight: 800; }
.nid-line-code-row { display: grid; grid-template-columns: minmax(150px, 0.35fr) auto minmax(260px, 1fr) auto; gap: 10px; align-items: end; }
.nid-line-row { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 10px; align-items: end; margin-top: 10px; }
.nid-line-row-rate { grid-template-columns: minmax(140px, 0.8fr) minmax(120px, 0.7fr) minmax(150px, 1fr) minmax(150px, 0.8fr); }
.nid-line-bottom { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 12px; padding-top: 10px; border-top: 1px solid var(--border); }
.nid-line-total { margin-left: auto; text-align: right; color: var(--fg); font-size: 15px; font-weight: 800; white-space: nowrap; }
.nid-line-meta { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 8px; }
.nid-chip { display: inline-flex; align-items: center; min-height: 22px; border: 1px solid var(--border); border-radius: 4px; background: var(--bg-tertiary); color: var(--fg-muted); padding: 2px 7px; font-size: 11px; font-weight: 800; }
.nid-rate-note { margin-top: 5px; color: var(--fg-muted); font-size: 11px; line-height: 1.35; }
.nid-rate-warning { color: var(--danger); font-weight: 800; }
.nid-table-wrap { overflow-x: auto; border: 1px solid var(--border); border-radius: 8px; background: var(--bg); }
.nid-table { width: 100%; min-width: 1180px; border-collapse: collapse; font-size: 12px; color: var(--fg); }
.nid-table th, .nid-table td { padding: 8px; border-bottom: 1px solid var(--border); text-align: left; vertical-align: top; }
.nid-table th { color: var(--fg-muted); background: var(--bg-tertiary); font-size: 11px; text-transform: uppercase; }
.nid-table .nid-input, .nid-table .nid-select { min-height: 34px; padding: 6px 8px; font-size: 12px; }
.nid-total-cell { font-weight: 800; white-space: nowrap; }
.nid-status { display: inline-flex; padding: 3px 8px; border: 1px solid var(--border); border-radius: 4px; background: var(--bg-tertiary); color: var(--fg-muted); font-size: 11px; font-weight: 800; white-space: nowrap; }
.nid-status-reviewed { border-color: var(--success); color: var(--success); }
.nid-status-needs-review { border-color: var(--warning); color: var(--warning); }
.nid-totals { display: grid; gap: 8px; max-width: 380px; margin-left: auto; }
.nid-total-row { display: flex; justify-content: space-between; gap: 16px; color: var(--fg); font-size: 14px; }
.nid-total-row strong { font-size: 18px; }
.nid-checklist { display: grid; gap: 8px; }
.nid-check { display: flex; justify-content: space-between; gap: 12px; padding: 9px 10px; border: 1px solid var(--border); border-radius: 6px; background: var(--bg); color: var(--fg); font-size: 13px; }
.nid-check-pass { border-color: var(--success); }
.nid-check-fail { border-color: var(--danger); }
.nid-check-state { font-weight: 800; white-space: nowrap; }
.nid-preview { position: relative; border: 1px solid var(--border); border-radius: 8px; background: var(--bg); color: var(--fg); padding: 18px; overflow: hidden; }
.nid-watermark { position: absolute; inset: 42% auto auto 12%; transform: rotate(-18deg); color: var(--fg-muted); font-size: 34px; font-weight: 800; opacity: 0.28; pointer-events: none; }
.nid-preview-head { display: flex; justify-content: space-between; gap: 16px; margin-bottom: 18px; position: relative; }
.nid-preview-title { margin: 0 0 8px; font-size: 26px; color: var(--fg); }
.nid-preview-meta { min-width: 250px; text-align: right; }
.nid-preview-party-row { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 28px; margin-bottom: 16px; padding-bottom: 14px; border-bottom: 1px solid var(--border); position: relative; }
.nid-preview-label { color: var(--fg-muted); font-size: 11px; font-weight: 800; text-transform: uppercase; }
.nid-preview-block { color: var(--fg); font-size: 13px; line-height: 1.5; white-space: pre-line; }
.nid-preview-muted { color: var(--fg-muted); }
.nid-preview-section { margin-top: 16px; position: relative; }
.nid-preview-section-title { margin: 0 0 6px; color: var(--fg); font-size: 13px; font-weight: 800; }
.nid-preview-table { width: 100%; border-collapse: collapse; margin: 16px 0; font-size: 12px; position: relative; }
.nid-preview-table th, .nid-preview-table td { padding: 8px; border-bottom: 1px solid var(--border); text-align: left; vertical-align: top; }
.nid-preview-table th { color: var(--fg-muted); background: var(--bg-secondary); text-transform: uppercase; font-size: 11px; }
.nid-preview-payto { margin-top: 18px; padding-top: 14px; border-top: 1px dotted var(--border); }
.nid-preview-footer { display: flex; justify-content: space-between; gap: 16px; margin-top: 18px; padding-top: 10px; border-top: 1px solid var(--border); color: var(--fg-muted); font-size: 11px; font-weight: 800; }
.nid-result { margin-top: 12px; padding: 12px; border: 1px solid var(--border); border-radius: 6px; background: var(--bg); color: var(--fg); font-size: 13px; line-height: 1.45; }
.nid-result-success { border-color: var(--success); color: var(--success); }
.nid-result-error { border-color: var(--danger); color: var(--danger); }
.nid-modal-backdrop { position: fixed; inset: 0; background: var(--bg-overlay); display: flex; align-items: center; justify-content: center; padding: 16px; z-index: 2000; }
.nid-modal { width: min(1180px, 90vw); max-height: 80vh; overflow: auto; border: 1px solid var(--border); border-radius: 8px; background: var(--bg-secondary); color: var(--fg); padding: 16px; box-shadow: var(--shadow-lg); }
.nid-modal-head { display: flex; justify-content: space-between; gap: 12px; align-items: flex-start; margin-bottom: 12px; }
.nid-modal-title { margin: 0; color: var(--fg); font-size: 18px; }
.nid-modal-tabs { display: flex; gap: 8px; margin: 12px 0; flex-wrap: wrap; }
.nid-modal-tab { min-height: 34px; border: 1px solid var(--border); border-radius: 6px; background: var(--bg); color: var(--fg-muted); padding: 7px 12px; font: inherit; font-size: 12px; font-weight: 800; cursor: pointer; }
.nid-modal-tab-active { border-color: var(--accent); color: var(--fg); background: var(--bg-tertiary); }
.nid-catalogue-search { min-height: 48px; font-size: 15px; }
.nid-results-count { margin-top: 10px; color: var(--fg-muted); font-size: 12px; font-weight: 800; }
.nid-cat-list { display: grid; gap: 8px; margin-top: 12px; }
.nid-cat-row { display: grid; grid-template-columns: minmax(150px, 0.4fr) minmax(0, 1fr) minmax(130px, auto) auto; gap: 10px; align-items: center; padding: 9px 10px; border: 1px solid var(--border); border-radius: 6px; background: var(--bg); color: var(--fg); font-size: 12px; }
.nid-cat-code { font-family: monospace; color: var(--accent); font-weight: 800; }
.nid-cat-name { min-width: 0; }
.nid-cat-price { text-align: right; color: var(--fg); font-weight: 800; white-space: nowrap; }
.nid-cat-group { margin-top: 12px; padding: 7px 9px; border: 1px solid var(--border); border-radius: 6px; background: var(--bg-tertiary); color: var(--fg); font-size: 12px; font-weight: 800; }
.nid-cat-category { border: 1px solid var(--border); border-radius: 8px; background: var(--bg); margin-top: 10px; overflow: hidden; }
.nid-cat-category-head { width: 100%; min-height: 42px; border: none; border-bottom: 1px solid var(--border); background: var(--bg-tertiary); color: var(--fg); padding: 9px 12px; display: flex; justify-content: space-between; gap: 12px; font: inherit; font-size: 13px; font-weight: 800; cursor: pointer; text-align: left; }
.nid-cat-category-items { display: grid; gap: 8px; padding: 10px 10px 10px 24px; }
.nid-disabled-note { display: block; margin-top: 3px; font-size: 11px; color: var(--fg-muted); }
.nid-future { margin-top: 14px; border: 1px solid var(--border); border-radius: 8px; background: var(--bg); color: var(--fg); padding: 10px 12px; }
.nid-future summary { cursor: pointer; color: var(--fg); font-size: 13px; font-weight: 800; }
.nid-future-list { margin: 10px 0 0; padding-left: 18px; color: var(--fg-muted); font-size: 12px; line-height: 1.55; }
@media (max-width: 768px) {
  .nid-page { padding: 16px; }
  .nid-topbar, .nid-preview-head, .nid-modal-head { flex-direction: column; }
  .nid-preview-party-row { grid-template-columns: 1fr; }
  .nid-preview-meta { min-width: 0; text-align: left; }
  .nid-badges { justify-content: flex-start; }
  .nid-grid { grid-template-columns: 1fr; }
  .nid-actions { align-items: stretch; }
  .nid-button { width: 100%; }
  .nid-totals { margin-left: 0; max-width: none; }
  .nid-line-code-row, .nid-line-row, .nid-line-row-rate, .nid-cat-row { grid-template-columns: 1fr; }
  .nid-line-bottom { flex-direction: column; align-items: stretch; }
  .nid-line-total { text-align: left; margin-left: 0; }
  .nid-cat-price { text-align: left; }
  .nid-preview-footer { flex-direction: column; }
}
@media print {
  .nid-topbar, .nid-warning, .nid-tabs, .nid-card:not(.nid-printable), .nid-actions, .nid-modal-backdrop { display: none !important; }
  .nid-page { padding: 0; }
  .nid-shell { max-width: none; }
  .nid-preview { border: none; border-radius: 0; }
}
`;
  document.head.appendChild(style);
}

function shell(title: string): HTMLElement {
  document.title = title;
  injectStyles();
  document.body.classList.add('sp-public-body');
  const root = (document.getElementById('app') ?? document.body) as HTMLElement;
  root.classList.add('sp-public-root');
  root.innerHTML = '';
  for (const id of ['sidebar-scrim', 'modal-overlay', 'toast']) {
    const el = document.getElementById(id);
    if (el) el.style.display = 'none';
  }
  const page = document.createElement('div');
  page.className = 'nid-page';
  root.appendChild(page);
  return page;
}

function esc(value: string): string {
  const el = document.createElement('div');
  el.textContent = value;
  return el.innerHTML;
}

function money(value: number): string {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(Number.isFinite(value) ? value : 0);
}

function providerDisplayName(): string {
  if (invoiceDraft.provider.providerType === 'company' || invoiceDraft.provider.providerType === 'organisation') {
    return invoiceDraft.provider.businessName || invoiceDraft.provider.supportWorkerName || invoiceDraft.session.providerName || 'Provider not entered';
  }
  return invoiceDraft.provider.supportWorkerName || invoiceDraft.provider.businessName || invoiceDraft.session.providerName || 'Provider not entered';
}

function participantDisplayName(): string {
  return invoiceDraft.participant.name || invoiceDraft.session.participantName || 'Participant not entered';
}

function providerDetailLines(): string[] {
  const lines: string[] = [];
  const workerName = invoiceDraft.provider.supportWorkerName;
  const bizName = invoiceDraft.provider.businessName;
  if (invoiceDraft.provider.providerType === 'sole-trader-trading' && workerName && bizName && workerName !== bizName) {
    lines.push(`Trading as: ${bizName}`);
  } else if ((invoiceDraft.provider.providerType === 'company' || invoiceDraft.provider.providerType === 'organisation') && workerName && bizName && workerName !== bizName) {
    lines.push(`Contact: ${workerName}`);
  }
  if (invoiceDraft.provider.abn) lines.push(`ABN ${invoiceDraft.provider.abn}`);
  if (invoiceDraft.provider.invoiceEmail) lines.push(invoiceDraft.provider.invoiceEmail);
  if (invoiceDraft.provider.phone) lines.push(invoiceDraft.provider.phone);
  if (invoiceDraft.provider.address) lines.push(invoiceDraft.provider.address);
  return lines;
}

function billToDetailLines(): string[] {
  return [
    participantDisplayName(),
    `NDIS/reference: ${invoiceDraft.participant.ndisNumber || 'Not entered'}`,
    `Billing type: ${invoiceDraft.participant.billingType}`,
    invoiceDraft.participant.billingType === 'plan-managed' ? `Plan manager: ${invoiceDraft.participant.planManagerName || 'Not entered'}` : '',
    invoiceDraft.participant.billingType === 'plan-managed' ? `Plan manager email: ${invoiceDraft.participant.planManagerEmail || 'Not entered'}` : '',
    invoiceDraft.participant.address,
  ].filter(line => line.trim().length > 0);
}

function lineTimeRange(item: InvoiceLineItem): string {
  const start = item.startTime || invoiceDraft.session.startTime;
  const end = item.endTime || invoiceDraft.session.endTime;
  if (start && end) return `${start}–${end}`;
  if (start) return `${start}–`;
  return '-';
}

function paymentDetailsLines(): string[] {
  const structuredLines: string[] = [];
  if (invoiceDraft.provider.paymentAccountName.trim()) structuredLines.push(`Account Name: ${invoiceDraft.provider.paymentAccountName.trim()}`);
  if (invoiceDraft.provider.paymentBSB.trim()) structuredLines.push(`BSB: ${invoiceDraft.provider.paymentBSB.trim()}`);
  if (invoiceDraft.provider.paymentAccountNumber.trim()) structuredLines.push(`Account Number: ${invoiceDraft.provider.paymentAccountNumber.trim()}`);
  const invoiceNumber = invoiceDraft.invoiceNumber.trim();
  const participantName = participantDisplayName();
  const customReference = invoiceDraft.provider.paymentReferenceCustom.trim();
  if (invoiceDraft.provider.paymentReferencePreference === 'custom' && customReference) {
    structuredLines.push(`Payment Reference: ${customReference}`);
  } else if (invoiceDraft.provider.paymentReferencePreference === 'participant-name' && participantName !== 'Participant not entered') {
    structuredLines.push(`Payment Reference: ${participantName}`);
  } else if (invoiceNumber) {
    structuredLines.push(`Payment Reference: Use invoice number (${invoiceNumber})`);
  }
  if (invoiceDraft.provider.paymentNotes.trim()) structuredLines.push(invoiceDraft.provider.paymentNotes.trim());
  if (structuredLines.length > 0) return structuredLines;

  const raw = invoiceDraft.provider.paymentDetailsPlaceholder.trim();
  const paymentReferenceFallback = invoiceNumber ? `Payment Reference: Use invoice number (${invoiceNumber})` : '';
  if (!raw) return paymentReferenceFallback ? ['Payment details not entered', paymentReferenceFallback] : ['Payment details not entered'];
  const fieldPatterns: Array<{ key: 'accountName' | 'bsb' | 'accountNumber' | 'paymentReference'; label: string; pattern: RegExp }> = [
    { key: 'accountName', label: 'Account Name', pattern: /\b(?:Account\s+Name|Name)\s*:?\s*([^\r\n]+)/i },
    { key: 'bsb', label: 'BSB', pattern: /\bBSB\s*:?\s*([0-9 -]+)/i },
    { key: 'accountNumber', label: 'Account Number', pattern: /\b(?:Account\s+Number|Account)\s*:?\s*([0-9 -]+)/i },
    { key: 'paymentReference', label: 'Payment Reference', pattern: /\b(?:Payment\s+Reference|Reference)\s*:?\s*([^\r\n]+)/i },
  ];
  const parsed = new Map<string, string>();
  let remainder = raw;
  for (const field of fieldPatterns) {
    const match = raw.match(field.pattern);
    if (!match?.[1]) continue;
    parsed.set(field.key, `${field.label}: ${match[1].trim()}`);
    remainder = remainder.replace(match[0], '').trim();
  }
  if (parsed.size === 0) {
    const rawLines = raw.split(/\r?\n/).map(line => line.trim()).filter(Boolean);
    return paymentReferenceFallback ? [...rawLines, paymentReferenceFallback] : rawLines;
  }
  const additionalLines = remainder
    .split(/\r?\n/)
    .map(line => line.replace(/^\s*[-,;|]\s*/, '').trim())
    .filter(Boolean);
  const parsedStructuredLines = fieldPatterns
    .map(field => parsed.get(field.key))
    .filter((line): line is string => Boolean(line));
  if (!parsed.has('paymentReference') && paymentReferenceFallback) return [...parsedStructuredLines, ...additionalLines, paymentReferenceFallback];
  return [...parsedStructuredLines, ...additionalLines];
}

function currentBillingSnapshot(): BillingIdentitySnapshot {
  const provider = invoiceDraft.provider;
  const isTrading = provider.providerType === 'sole-trader-trading';
  const isEntity = provider.providerType === 'company' || provider.providerType === 'organisation';
  return {
    providerType: provider.providerType,
    legalName: isEntity ? provider.businessName : provider.supportWorkerName,
    tradingName: isTrading ? provider.businessName : '',
    businessName: provider.businessName,
    abn: provider.abn,
    invoiceEmail: provider.invoiceEmail,
    phone: provider.phone,
    address: provider.address,
    gstRegistered: provider.gstRegistered,
    paymentAccountName: provider.paymentAccountName,
    paymentBSB: provider.paymentBSB,
    paymentAccountNumber: provider.paymentAccountNumber,
    paymentReferencePreference: provider.paymentReferencePreference,
    paymentReferenceCustom: provider.paymentReferenceCustom,
    paymentNotes: provider.paymentNotes,
    snapshotAt: new Date().toISOString(),
  };
}

function snapshotBillingIdentity(): void {
  invoiceDraft.billingSnapshot = currentBillingSnapshot();
}

function numberValue(value: string): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function parseRate(value: string): number {
  return parseCatalogueRate(value);
}

function calculateTimeDuration(startTime: string, endTime: string, breakMinutes: number): number {
  if (!startTime || !endTime) return 0;
  const [startHour = '0', startMinute = '0'] = startTime.split(':');
  const [endHour = '0', endMinute = '0'] = endTime.split(':');
  const start = Number(startHour) * 60 + Number(startMinute);
  const end = Number(endHour) * 60 + Number(endMinute);
  const minutes = Math.max(0, end - start - breakMinutes);
  return Math.round((minutes / 60) * 100) / 100;
}

function calculateDuration(session: SupportSessionDraft): number {
  return calculateTimeDuration(session.startTime, session.endTime, session.breakMinutes);
}

function calculateLineDuration(item: InvoiceLineItem): number {
  return calculateTimeDuration(item.startTime, item.endTime, invoiceDraft.session.breakMinutes);
}

function lineTotal(item: InvoiceLineItem): number {
  return item.quantity * item.rate + item.gstComponent;
}

function subtotal(): number {
  return invoiceDraft.lineItems.reduce((sum, item) => sum + item.quantity * item.rate, 0);
}

function gstTotal(): number {
  return invoiceDraft.lineItems.reduce((sum, item) => sum + item.gstComponent, 0);
}

function grandTotal(): number {
  return subtotal() + gstTotal();
}

function catalogueReference(item: CatalogueItem): CatalogueReference {
  return {
    supportItemNumber: item.supportItemNumber,
    supportItemName: item.supportItemName,
    supportCategory: item.supportCategory,
    registrationGroup: item.registrationGroup,
    unit: item.unit,
    priceLimit: item.priceLimit,
    nonFaceToFace: item.nonFaceToFace,
    providerTravel: item.providerTravel,
    shortNoticeCancellation: item.shortNoticeCancellation,
  };
}

function cataloguePriceLimit(item: InvoiceLineItem): number | null {
  const priceLimit = item.catalogueRef?.priceLimit ?? '';
  const parsed = parseRate(priceLimit);
  return parsed > 0 ? parsed : null;
}

function duplicateLineItem(item: InvoiceLineItem): InvoiceLineItem {
  return {
    ...item,
    id: crypto.randomUUID(),
    catalogueRef: item.catalogueRef ? { ...item.catalogueRef } : undefined,
  };
}

function applyCatalogueItem(line: InvoiceLineItem, catalogueItem: CatalogueItem): void {
  line.supportItemCode = catalogueItem.supportItemNumber;
  line.supportItemName = catalogueItem.supportItemName;
  line.unit = normaliseUnit(catalogueItem.unit);
  line.rate = parseRate(catalogueItem.priceLimit);
  line.claimType = line.claimType || 'standard';
  line.reviewStatus = 'needs-review';
  line.source = 'catalogue-matched';
  line.catalogueRef = catalogueReference(catalogueItem);
  line.date = line.date || invoiceDraft.session.serviceDate;
  line.startTime = line.startTime || invoiceDraft.session.startTime;
  line.endTime = line.endTime || invoiceDraft.session.endTime;
  line.duration = calculateLineDuration(line);
  if (line.unit === 'Hour' && line.duration > 0) line.quantity = line.duration;
}

function unitFromParticipantCode(unit: string): InvoiceLineItem['unit'] {
  const lower = unit.toLowerCase();
  if (lower.includes('km')) return 'Km';
  if (lower.includes('day')) return 'Day';
  if (lower.includes('each')) return 'Each';
  return 'Hour';
}

function catalogueReferenceFromParticipantCode(code: ParticipantApplicableCode): CatalogueReference {
  return {
    supportItemNumber: code.supportItemCode,
    supportItemName: code.supportItemName,
    supportCategory: code.category,
    registrationGroup: code.registrationGroup,
    unit: code.unit,
    priceLimit: String(code.priceLimit),
    nonFaceToFace: false,
    providerTravel: false,
    shortNoticeCancellation: false,
  };
}

function applyParticipantCode(line: InvoiceLineItem, code: ParticipantApplicableCode): void {
  line.supportItemCode = code.supportItemCode;
  line.supportItemName = code.supportItemName;
  line.unit = unitFromParticipantCode(code.unit);
  line.rate = code.priceLimit;
  line.claimType = code.claimType || 'standard';
  line.reviewStatus = 'draft';
  line.source = 'participant-approved';
  line.catalogueRef = catalogueReferenceFromParticipantCode(code);
  line.date = line.date || invoiceDraft.session.serviceDate;
  line.startTime = line.startTime || invoiceDraft.session.startTime;
  line.endTime = line.endTime || invoiceDraft.session.endTime;
  line.duration = calculateLineDuration(line);
  if (line.unit === 'Hour' && line.duration > 0) line.quantity = line.duration;
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return { ...fallback, ...(JSON.parse(raw) as Partial<T>) };
  } catch {
    return fallback;
  }
}

function normalizeParticipantProfile(parsed: Partial<ParticipantProfileDraft>): ParticipantProfileDraft {
  const fallback = defaultParticipant();
  const fallbackContext: ParticipantPlanContext = {
    planStartDate: '',
    planEndDate: '',
    planManagementType: 'unknown',
    budgets: [],
    participantNameFromPlan: '',
    ndisNumberFromPlan: '',
    warnings: [],
    sourceType: 'manual',
    sourceFileName: '',
    extractedAt: '',
  };
  return {
    ...fallback,
    ...parsed,
    applicableCodes: Array.isArray(parsed.applicableCodes) ? parsed.applicableCodes : [],
    planContext: parsed.planContext
      ? {
        ...fallbackContext,
        ...parsed.planContext,
        budgets: Array.isArray(parsed.planContext.budgets) ? parsed.planContext.budgets : [],
      }
      : undefined,
  };
}

function readParticipantProfile(): ParticipantProfileDraft {
  try {
    const raw = localStorage.getItem(PARTICIPANT_STORAGE_KEY);
    if (!raw) return defaultParticipant();
    return normalizeParticipantProfile(JSON.parse(raw) as Partial<ParticipantProfileDraft>);
  } catch {
    return defaultParticipant();
  }
}

function readInvoice(): InvoiceDraft {
  const fallback = defaultInvoiceDraft();
  try {
    const raw = localStorage.getItem(INVOICE_STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<InvoiceDraft>;
    return {
      ...fallback,
      ...parsed,
      participant: { ...fallback.participant, ...parsed.participant },
      provider: { ...fallback.provider, ...parsed.provider },
      session: { ...fallback.session, ...parsed.session },
      lineItems: Array.isArray(parsed.lineItems) ? parsed.lineItems.map(item => ({ ...defaultLineItem(), ...item, source: item.source ?? 'manual-needs-review' })) : [],
      status: 'Draft',
    };
  } catch {
    return fallback;
  }
}

function saveInvoice(): void {
  localStorage.setItem(INVOICE_STORAGE_KEY, JSON.stringify(invoiceDraft));
}

function loadParticipantProfile(): void {
  invoiceDraft.participant = readParticipantProfile();
  invoiceDraft.session.participantName = invoiceDraft.participant.name;
}

function loadProviderProfile(): void {
  invoiceDraft.provider = readJson(PROVIDER_STORAGE_KEY, defaultProvider());
  invoiceDraft.session.providerName = invoiceDraft.provider.businessName || invoiceDraft.provider.supportWorkerName;
  if (!invoiceDraft.session.kmRate) invoiceDraft.session.kmRate = parseRate(invoiceDraft.provider.defaultKmRate);
}

function applyDemoDefaults(): void {
  const day = today();
  const hasSavedParticipant = localStorage.getItem(PARTICIPANT_STORAGE_KEY) !== null;
  const hasSavedProvider = localStorage.getItem(PROVIDER_STORAGE_KEY) !== null;

  if (!invoiceDraft.participant.name.trim() && !hasSavedParticipant) {
    invoiceDraft.participant = {
      ...invoiceDraft.participant,
      name: 'Jane Smith',
      ndisNumber: 'DEMO-4821957',
      billingType: 'plan-managed',
      planManagerName: 'Plan Partners Australia',
      planManagerEmail: 'claims@planpartners.example.com',
      phone: '0412 345 678',
      address: '42 Wattle St, Richmond VIC 3121',
      invoiceNotes: 'Demo participant for testing',
      preferredInvoiceRecipient: 'Plan manager',
      applicableCodes: [],
    };
  }

  if (!invoiceDraft.provider.businessName.trim() && !hasSavedProvider) {
    invoiceDraft.provider = {
      ...invoiceDraft.provider,
      providerType: 'sole-trader-trading',
      businessName: 'Sunrise Support Services',
      supportWorkerName: 'Alex Johnson',
      abn: '51 824 753 556',
      invoiceEmail: 'invoices@sunrise-support.example.com',
      phone: '0398 765 432',
      address: '15 Banksia Ave, Hawthorn VIC 3122',
      serviceTypes: 'Assistance with daily life, Community participation',
      defaultHourlyRate: '65.09',
      defaultKmRate: '0.97',
      gstRegistered: 'no',
      paymentTerms: '14 days from invoice date',
      invoiceFooter: 'Thank you for choosing Sunrise Support Services',
      paymentAccountName: 'Sunrise Support Services',
      paymentBSB: '063-000',
      paymentAccountNumber: '1234 5678',
      paymentReferencePreference: 'invoice-number',
      paymentReferenceCustom: '',
      paymentNotes: 'Demo payment details only.',
      paymentDetailsPlaceholder: 'Account Name: Sunrise Support Services\nBSB: 063-000\nAccount Number: 1234 5678\nPayment Reference: INV-2026-001',
    };
  }

  if (!invoiceDraft.session.participantName.trim()
    && !invoiceDraft.session.providerName.trim()
    && !invoiceDraft.session.startTime
    && !invoiceDraft.session.endTime
    && !invoiceDraft.session.supportNotes.trim()) {
    invoiceDraft.session = {
      ...invoiceDraft.session,
      participantName: 'Jane Smith',
      providerName: 'Alex Johnson',
      serviceDate: day,
      startTime: '09:00',
      endTime: '12:00',
      breakMinutes: 15,
      calculatedDuration: 2.75,
      serviceMode: 'in-person',
      location: 'Participant home',
      supportNotes: 'Demo session for testing',
    };
  }

  if (!invoiceDraft.invoiceNumber.trim()) invoiceDraft.invoiceNumber = 'INV-2026-001';
  if (!invoiceDraft.invoiceDate) invoiceDraft.invoiceDate = day;
  if (!invoiceDraft.serviceDate) invoiceDraft.serviceDate = day;
}

function syncCalculatedDuration(): void {
  invoiceDraft.session.calculatedDuration = calculateDuration(invoiceDraft.session);
  invoiceDraft.serviceDate = invoiceDraft.session.serviceDate;
  invoiceDraft.lineItems = invoiceDraft.lineItems.map(item => ({
    ...item,
    date: item.date || invoiceDraft.session.serviceDate,
    startTime: item.startTime || invoiceDraft.session.startTime,
    endTime: item.endTime || invoiceDraft.session.endTime,
    duration: item.startTime && item.endTime ? calculateLineDuration(item) : item.duration,
  }));
}

function input(field: string, label: string, value: string, type = 'text', wide = false, extra = ''): string {
  return `
    <label class="nid-field${wide ? ' nid-field-wide' : ''}">
      <span class="nid-label">${esc(label)}</span>
      <input class="nid-input" data-field="${esc(field)}" type="${esc(type)}" value="${esc(value)}" ${extra}>
    </label>
  `;
}

function textarea(field: string, label: string, value: string): string {
  return `
    <label class="nid-field nid-field-wide">
      <span class="nid-label">${esc(label)}</span>
      <textarea class="nid-textarea" data-field="${esc(field)}">${esc(value)}</textarea>
    </label>
  `;
}

function select(field: string, label: string, value: string, options: readonly string[]): string {
  return `
    <label class="nid-field">
      <span class="nid-label">${esc(label)}</span>
      <select class="nid-select" data-field="${esc(field)}">
        ${options.map(option => `<option value="${esc(option)}"${option === value ? ' selected' : ''}>${esc(option)}</option>`).join('')}
      </select>
    </label>
  `;
}

function providerTypeSelect(field: string, label: string, value: ProviderProfileDraft['providerType']): string {
  const options: Array<{ value: ProviderProfileDraft['providerType']; label: string }> = [
    { value: 'individual', label: 'Individual / sole trader' },
    { value: 'sole-trader-trading', label: 'Sole trader with trading name' },
    { value: 'company', label: 'Company / Pty Ltd' },
    { value: 'organisation', label: 'Organisation / provider business' },
  ];
  return `
    <label class="nid-field">
      <span class="nid-label">${esc(label)}</span>
      <select class="nid-select" data-field="${esc(field)}">
        ${options.map(option => `<option value="${esc(option.value)}"${option.value === value ? ' selected' : ''}>${esc(option.label)}</option>`).join('')}
      </select>
    </label>
  `;
}

function paymentReferenceSelect(field: string, label: string, value: ProviderProfileDraft['paymentReferencePreference']): string {
  const options: Array<{ value: ProviderProfileDraft['paymentReferencePreference']; label: string }> = [
    { value: 'invoice-number', label: 'Invoice number' },
    { value: 'participant-name', label: 'Participant name' },
    { value: 'custom', label: 'Custom' },
  ];
  return `
    <label class="nid-field">
      <span class="nid-label">${esc(label)}</span>
      <select class="nid-select" data-field="${esc(field)}">
        ${options.map(option => `<option value="${esc(option.value)}"${option.value === value ? ' selected' : ''}>${esc(option.label)}</option>`).join('')}
      </select>
    </label>
  `;
}

function renderTabs(): string {
  const tabs: Array<{ id: InvoiceTab; label: string }> = [
    { id: 'session', label: 'Session & Profiles' },
    { id: 'details', label: 'Invoice Details' },
    { id: 'lines', label: 'Line Items' },
    { id: 'validation', label: 'Totals & Validation' },
    { id: 'preview', label: 'Preview & Send' },
  ];
  return `<div class="nid-tabs">${tabs.map(tab => `<button class="nid-tab ${activeTab === tab.id ? 'nid-tab-active' : ''}" type="button" data-tab="${tab.id}">${esc(tab.label)}</button>`).join('')}</div>`;
}

function renderSessionTab(): string {
  return `
    <section class="nid-card">
      <h2>Participant</h2>
      <div class="nid-actions">
        <button class="nid-button nid-button-secondary" id="nid-load-participant" type="button">Load saved participant</button>
        <a class="nid-button nid-button-secondary" href="/people/test/support-v2-draft/participant-profile-builder" target="_blank" rel="noopener">Open participant builder</a>
      </div>
      <div class="nid-grid">
        ${input('participant.name', 'Participant name', invoiceDraft.participant.name)}
        ${input('participant.ndisNumber', 'NDIS/reference', invoiceDraft.participant.ndisNumber)}
        ${select('participant.billingType', 'Billing type', invoiceDraft.participant.billingType, ['unknown', 'plan-managed', 'self-managed', 'ndia-managed'])}
        ${input('participant.planManagerName', 'Plan manager/billing contact name', invoiceDraft.participant.planManagerName)}
        ${input('participant.planManagerEmail', 'Plan manager email', invoiceDraft.participant.planManagerEmail)}
        ${input('participant.preferredInvoiceRecipient', 'Preferred invoice recipient', invoiceDraft.participant.preferredInvoiceRecipient)}
        ${input('participant.phone', 'Phone (optional)', invoiceDraft.participant.phone)}
        ${input('participant.address', 'Address (optional)', invoiceDraft.participant.address)}
        ${textarea('participant.invoiceNotes', 'Invoice notes', invoiceDraft.participant.invoiceNotes)}
      </div>
    </section>
    ${renderParticipantPlanSummary()}
    <section class="nid-card">
      <h2>Provider</h2>
      <div class="nid-actions">
        <button class="nid-button nid-button-secondary" id="nid-load-provider" type="button">Load saved provider</button>
        <a class="nid-button nid-button-secondary" href="/people/test/support-v2-draft/provider-profile-builder" target="_blank" rel="noopener">Open provider builder</a>
      </div>
      <div class="nid-grid">
        ${providerTypeSelect('provider.providerType', 'Provider type', invoiceDraft.provider.providerType)}
        ${input('provider.businessName', 'Business/provider name', invoiceDraft.provider.businessName)}
        ${input('provider.supportWorkerName', 'Support worker display name', invoiceDraft.provider.supportWorkerName)}
        ${input('provider.abn', 'ABN', invoiceDraft.provider.abn)}
        ${input('provider.invoiceEmail', 'Invoice email', invoiceDraft.provider.invoiceEmail)}
        ${input('provider.phone', 'Phone (optional)', invoiceDraft.provider.phone)}
        ${input('provider.address', 'Address (optional)', invoiceDraft.provider.address)}
        ${input('provider.serviceTypes', 'Service types', invoiceDraft.provider.serviceTypes, 'text', true)}
        ${input('provider.defaultHourlyRate', 'Default hourly rate placeholder', invoiceDraft.provider.defaultHourlyRate)}
        ${input('provider.defaultKmRate', 'Default km rate placeholder', invoiceDraft.provider.defaultKmRate)}
        ${select('provider.gstRegistered', 'GST registered', invoiceDraft.provider.gstRegistered, ['unknown', 'yes', 'no'])}
        ${input('provider.paymentAccountName', 'Payment account name', invoiceDraft.provider.paymentAccountName)}
        ${input('provider.paymentBSB', 'BSB', invoiceDraft.provider.paymentBSB)}
        ${input('provider.paymentAccountNumber', 'Account number', invoiceDraft.provider.paymentAccountNumber)}
        ${paymentReferenceSelect('provider.paymentReferencePreference', 'Payment reference preference', invoiceDraft.provider.paymentReferencePreference)}
        ${invoiceDraft.provider.paymentReferencePreference === 'custom' ? input('provider.paymentReferenceCustom', 'Custom payment reference', invoiceDraft.provider.paymentReferenceCustom) : ''}
        ${textarea('provider.paymentNotes', 'Payment notes', invoiceDraft.provider.paymentNotes)}
        ${textarea('provider.paymentTerms', 'Payment terms', invoiceDraft.provider.paymentTerms)}
        ${textarea('provider.invoiceFooter', 'Invoice footer/details', invoiceDraft.provider.invoiceFooter)}
        ${textarea('provider.paymentDetailsPlaceholder', 'Advanced fallback payment details', invoiceDraft.provider.paymentDetailsPlaceholder)}
      </div>
    </section>
    <section class="nid-card">
      <h2>Session Entry</h2>
      <div class="nid-grid">
        ${input('session.participantName', 'Session participant', invoiceDraft.session.participantName || invoiceDraft.participant.name)}
        ${input('session.providerName', 'Session provider', invoiceDraft.session.providerName || invoiceDraft.provider.businessName)}
        ${input('session.serviceDate', 'Service date', invoiceDraft.session.serviceDate, 'date')}
        ${input('session.startTime', 'Start time', invoiceDraft.session.startTime, 'time')}
        ${input('session.endTime', 'End time', invoiceDraft.session.endTime, 'time')}
        ${input('session.breakMinutes', 'Break minutes', String(invoiceDraft.session.breakMinutes), 'number', false, 'min="0" step="1"')}
        <div class="nid-field"><span class="nid-label">Calculated duration</span><div class="nid-readonly">${invoiceDraft.session.calculatedDuration.toFixed(2)} hours</div></div>
        ${select('session.serviceMode', 'Service mode', invoiceDraft.session.serviceMode, ['in-person', 'phone', 'video', 'community-access', 'other'])}
        ${input('session.location', 'Location', invoiceDraft.session.location)}
        ${input('session.kmTravelled', 'Km travelled', String(invoiceDraft.session.kmTravelled), 'number', false, 'min="0" step="0.01"')}
        ${input('session.kmRate', 'Km rate', String(invoiceDraft.session.kmRate), 'number', false, 'min="0" step="0.01"')}
        ${textarea('session.supportNotes', 'Support notes', invoiceDraft.session.supportNotes)}
      </div>
    </section>
  `;
}

function renderParticipantPlanSummary(): string {
  const context = invoiceDraft.participant.planContext;
  if (!context) {
    return `
      <section class="nid-card">
        <h2>Participant Plan Context</h2>
        <div class="nid-warning">No plan context saved. Add plan details in the Participant Profile Builder.</div>
        <div class="nid-actions">
          <a class="nid-button nid-button-secondary" href="/people/test/support-v2-draft/participant-profile-builder" target="_blank" rel="noopener">Edit in Participant Profile Builder</a>
        </div>
      </section>
    `;
  }
  const budgetRows = context.budgets.length === 0
    ? '<div class="nid-help">No budget categories saved.</div>'
    : `<div class="nid-table-wrap"><table class="nid-table"><thead><tr><th>Category</th><th>Amount</th><th>Management</th></tr></thead><tbody>${context.budgets.map(budget => `
      <tr><td>${esc(budget.category)}</td><td>${money(budget.amount)}</td><td>${esc(budget.managementType || context.planManagementType)}</td></tr>
    `).join('')}</tbody></table></div>`;
  return `
    <section class="nid-card">
      <h2>Participant Plan Context</h2>
      <div class="nid-grid">
        <div class="nid-field"><span class="nid-label">Plan dates</span><div class="nid-readonly">${esc(context.planStartDate || 'Not entered')} to ${esc(context.planEndDate || 'Not entered')}</div></div>
        <div class="nid-field"><span class="nid-label">Management type</span><div class="nid-readonly">${esc(context.planManagementType)}</div></div>
        <div class="nid-field"><span class="nid-label">Name from plan</span><div class="nid-readonly">${esc(context.participantNameFromPlan || 'Not entered')}</div></div>
        <div class="nid-field"><span class="nid-label">NDIS number from plan</span><div class="nid-readonly">${esc(context.ndisNumberFromPlan || 'Not entered')}</div></div>
      </div>
      ${budgetRows}
      <div class="nid-help">Plan budgets and funding categories are not the same as claimable invoice line items. Support item codes must be selected and reviewed separately.</div>
      <div class="nid-actions">
        <a class="nid-button nid-button-secondary" href="/people/test/support-v2-draft/participant-profile-builder" target="_blank" rel="noopener">Edit in Participant Profile Builder</a>
      </div>
    </section>
  `;
}

function renderDetailsTab(): string {
  return `
    <section class="nid-card">
      <h2>Invoice Details</h2>
      <div class="nid-grid">
        <label class="nid-field">
          <span class="nid-label">Invoice number</span>
          <input class="nid-input" data-field="invoiceNumber" type="text" value="${esc(invoiceDraft.invoiceNumber)}">
          ${invoiceDraft.invoiceNumber.trim() ? '' : '<div class="nid-error-text">Invoice number required</div>'}
          <div class="nid-help">Manual invoice number. Confirm this number has not already been used.</div>
        </label>
        <div class="nid-field"><span class="nid-label">Suggest next</span><button class="nid-button nid-button-secondary" id="nid-suggest-next" type="button">Suggest next</button></div>
        <label class="nid-field">
          <span class="nid-label">Invoice date</span>
          <input class="nid-input" data-field="invoiceDate" type="date" value="${esc(invoiceDraft.invoiceDate)}">
          <div class="nid-help">Invoice date is the date this invoice draft is issued. Service date is when the support occurred.</div>
        </label>
        ${input('serviceDate', 'Service date', invoiceDraft.serviceDate, 'date')}
        ${input('dueDate', 'Due date (optional)', invoiceDraft.dueDate, 'date')}
        ${textarea('referenceNote', 'Reference note (optional)', invoiceDraft.referenceNote)}
      </div>
    </section>
  `;
}

function renderLineItemsTab(): string {
  const catalogueLoaded = loadCatalogue().length > 0;
  return `
    <section class="nid-card">
      <h2>Line Items</h2>
      ${renderParticipantCodesPicker()}
      ${catalogueLoaded ? '' : '<div class="nid-warning">Upload official NDIS Support Catalogue XLSX on the NDIS Extraction page for code search</div>'}
      <div class="nid-actions">
        <button class="nid-button" id="nid-add-blank-line" type="button">Manual Entry</button>
        <button class="nid-button nid-button-secondary" id="nid-add-from-catalogue" type="button">Search Full Catalogue</button>
        <button class="nid-button nid-button-secondary" id="nid-duplicate-last-line" type="button" ${invoiceDraft.lineItems.length === 0 ? 'disabled' : ''}>Duplicate last line</button>
      </div>
      <div class="nid-line-list">${invoiceDraft.lineItems.map(renderLineCard).join('')}</div>
    </section>
  `;
}

function approvedParticipantCodes(): ParticipantApplicableCode[] {
  return invoiceDraft.participant.applicableCodes.filter(code => code.reviewStatus === 'approved');
}

function isApprovedParticipantCode(supportItemCode: string): boolean {
  const normalized = supportItemCode.trim();
  return normalized.length > 0 && approvedParticipantCodes().some(code => code.supportItemCode === normalized);
}

function renderParticipantCodesPicker(): string {
  const codes = approvedParticipantCodes();
  if (codes.length === 0) {
    return '<div class="nid-warning">No applicable line codes saved for this participant. Add codes from the Participant Profile Builder or search the catalogue manually.</div>';
  }
  return `
    <div class="nid-participant-code-panel">
      <h3>Participant Codes</h3>
      <div class="nid-help">Approved participant-specific codes appear first. Review dates, rate, unit, and claim type before using.</div>
      <div class="nid-cat-list">
        ${codes.map(code => `
          <div class="nid-cat-row">
            <div class="nid-cat-code">${esc(code.supportItemCode)}</div>
            <div class="nid-cat-name">${esc(code.supportItemName)}<div class="nid-help">${esc(code.category)} ${esc(code.registrationGroup)}</div></div>
            <div class="nid-cat-price">${money(code.priceLimit)}/${esc(code.unit || 'unit')}</div>
            <button class="nid-button nid-button-secondary" type="button" data-use-participant-code="${esc(code.supportItemCode)}">Use Code</button>
          </div>
        `).join('')}
      </div>
    </div>
  `;
}

function renderLineCard(item: InvoiceLineItem): string {
  const units: InvoiceLineItem['unit'][] = ['Hour', 'Each', 'Km', 'Day'];
  const statuses: InvoiceLineItem['reviewStatus'][] = ['draft', 'reviewed', 'needs-review'];
  const priceLimit = cataloguePriceLimit(item);
  const limitText = priceLimit === null ? '' : `NDIA limit: ${money(priceLimit)}/${item.catalogueRef?.unit || item.unit}`;
  const overLimit = priceLimit !== null && item.rate > priceLimit;
  const notApproved = item.supportItemCode.trim().length > 0 && !isApprovedParticipantCode(item.supportItemCode);
  const badges = [
    item.source ? `<span class="nid-chip">${esc(item.source)}</span>` : '',
    item.catalogueRef?.registrationGroup ? `<span class="nid-chip">${esc(item.catalogueRef.registrationGroup)}</span>` : '',
    item.catalogueRef?.supportCategory ? `<span class="nid-chip">${esc(item.catalogueRef.supportCategory)}</span>` : '',
    item.catalogueRef?.nonFaceToFace ? '<span class="nid-chip">Non-face-to-face</span>' : '',
    item.catalogueRef?.providerTravel ? '<span class="nid-chip">Provider travel</span>' : '',
    item.catalogueRef?.shortNoticeCancellation ? '<span class="nid-chip">Short notice cancellation</span>' : '',
  ].filter(Boolean).join('');
  return `
    <article class="nid-line-card" data-line-id="${esc(item.id)}">
      ${item.reviewStatus === 'needs-review' ? '<div class="nid-line-review">Needs review before use</div>' : ''}
      ${notApproved ? '<div class="nid-line-review">Not in participant approved codes</div>' : ''}
      <div class="nid-line-code-row">
        <label class="nid-field">
          <span class="nid-label">Support item code</span>
          <input class="nid-input" data-line-field="supportItemCode" value="${esc(item.supportItemCode)}">
          ${item.supportItemCode.trim() ? '' : '<div class="nid-help">No code — search catalogue or enter manually</div>'}
        </label>
        <button class="nid-button" type="button" data-catalogue-line="${esc(item.id)}">Search catalogue</button>
        <label class="nid-field">
          <span class="nid-label">Support item name</span>
          <input class="nid-input" data-line-field="supportItemName" value="${esc(item.supportItemName)}">
        </label>
        <button class="nid-button nid-button-danger" type="button" data-remove-line="${esc(item.id)}">Remove</button>
      </div>
      ${badges ? `<div class="nid-line-meta">${badges}</div>` : ''}
      <div class="nid-line-row">
        <label class="nid-field"><span class="nid-label">Date</span><input class="nid-input" data-line-field="date" type="date" value="${esc(item.date)}"></label>
        <label class="nid-field"><span class="nid-label">Start time</span><input class="nid-input" data-line-field="startTime" type="time" value="${esc(item.startTime)}"></label>
        <label class="nid-field"><span class="nid-label">End time</span><input class="nid-input" data-line-field="endTime" type="time" value="${esc(item.endTime)}"></label>
        <label class="nid-field"><span class="nid-label">Duration</span><input class="nid-input" data-line-field="duration" type="number" min="0" step="0.01" value="${item.duration}"></label>
        <label class="nid-field"><span class="nid-label">Unit</span><select class="nid-select" data-line-field="unit">${units.map(unit => `<option value="${unit}"${item.unit === unit ? ' selected' : ''}>${unit}</option>`).join('')}</select></label>
        <label class="nid-field"><span class="nid-label">Quantity</span><input class="nid-input" data-line-field="quantity" type="number" min="0" step="0.01" value="${item.quantity}"></label>
      </div>
      <div class="nid-line-row nid-line-row-rate">
        <label class="nid-field">
          <span class="nid-label">Rate</span>
          <input class="nid-input" data-line-field="rate" type="number" min="0" step="0.01" value="${item.rate}">
          ${limitText ? `<div class="nid-rate-note">${esc(limitText)}</div>` : ''}
          ${overLimit && priceLimit !== null ? `<div class="nid-rate-note nid-rate-warning">Rate exceeds NDIA price limit (${money(priceLimit)})</div>` : ''}
        </label>
        <label class="nid-field"><span class="nid-label">GST</span><input class="nid-input" data-line-field="gstComponent" type="number" min="0" step="0.01" value="${item.gstComponent}"></label>
        <label class="nid-field"><span class="nid-label">Claim type</span><input class="nid-input" data-line-field="claimType" value="${esc(item.claimType)}"></label>
        <label class="nid-field"><span class="nid-label">Review status</span><select class="nid-select" data-line-field="reviewStatus">${statuses.map(status => `<option value="${status}"${item.reviewStatus === status ? ' selected' : ''}>${status}</option>`).join('')}</select></label>
      </div>
      <div class="nid-line-bottom">
        <span class="nid-status nid-status-${esc(item.reviewStatus)}">${esc(item.reviewStatus)}</span>
        <div class="nid-line-total">Total ${money(lineTotal(item))}</div>
      </div>
    </article>
  `;
}

function validationChecks(): Array<{ label: string; pass: boolean; issue?: InvoiceValidationIssue }> {
  const hasBillingEmail = invoiceDraft.participant.billingType !== 'plan-managed' || invoiceDraft.participant.planManagerEmail.trim().length > 0;
  const firstLine = invoiceDraft.lineItems[0] ?? null;
  return [
    { label: 'Invoice number present', pass: invoiceDraft.invoiceNumber.trim().length > 0, issue: { field: 'invoiceNumber', message: 'Invoice number required', severity: 'error' } },
    { label: 'Participant name present', pass: invoiceDraft.participant.name.trim().length > 0 || invoiceDraft.session.participantName.trim().length > 0 },
    { label: 'NDIS/reference present', pass: invoiceDraft.participant.ndisNumber.trim().length > 0 },
    { label: 'Billing email present if plan-managed', pass: hasBillingEmail },
    { label: 'Provider ABN present or exemption note', pass: invoiceDraft.provider.abn.trim().length > 0 || invoiceDraft.provider.invoiceFooter.toLowerCase().includes('exemption') },
    { label: 'Support date/times present', pass: Boolean(invoiceDraft.session.serviceDate && invoiceDraft.session.startTime && invoiceDraft.session.endTime) },
    { label: 'Support item code present', pass: invoiceDraft.lineItems.length > 0 && invoiceDraft.lineItems.every(item => item.supportItemCode.trim().length > 0) },
    { label: 'Claim type present', pass: invoiceDraft.lineItems.length > 0 && invoiceDraft.lineItems.every(item => item.claimType.trim().length > 0) },
    { label: 'Unit/rate/quantity present', pass: Boolean(firstLine) && invoiceDraft.lineItems.every(item => item.unit && item.rate > 0 && item.quantity > 0) },
    { label: 'One participant only', pass: true },
  ];
}

function renderValidationTab(): string {
  const checks = validationChecks();
  const failed = checks.filter(check => !check.pass).length;
  return `
    <section class="nid-card">
      <h2>Totals</h2>
      <div class="nid-totals">
        <div class="nid-total-row"><span>Subtotal</span><strong>${money(subtotal())}</strong></div>
        <div class="nid-total-row"><span>GST total</span><strong>${money(gstTotal())}</strong></div>
        <div class="nid-total-row"><span>Grand total</span><strong>${money(grandTotal())}</strong></div>
        <div class="nid-total-row"><span>Amount due</span><strong>${money(grandTotal())}</strong></div>
      </div>
    </section>
    <section class="nid-card">
      <h2>Validation checklist</h2>
      <div class="nid-warning">Warnings count: ${failed}. Draft/manual first. Review required before use.</div>
      <div class="nid-checklist">
        ${checks.map(check => `<div class="nid-check ${check.pass ? 'nid-check-pass' : 'nid-check-fail'}"><span>${esc(check.label)}</span><span class="nid-check-state">${check.pass ? 'Pass' : 'Fail'}</span></div>`).join('')}
      </div>
    </section>
  `;
}

function invoicePlainText(): string {
  const participantName = invoiceDraft.participant.name || invoiceDraft.session.participantName || 'Participant not entered';
  const lines = invoiceDraft.lineItems.map(item => `${item.date} ${item.supportItemCode} ${item.supportItemName} ${item.quantity} ${item.unit} x ${money(item.rate)} = ${money(lineTotal(item))}`).join('\n');
  return [
    `TEST DRAFT Invoice ${invoiceDraft.invoiceNumber || '[no invoice number]'} -- ${participantName}`,
    'DRAFT -- REVIEW REQUIRED',
    'No NDIS claim submitted. Not sent. Manual review required.',
    '',
    `Provider: ${invoiceDraft.provider.businessName || invoiceDraft.session.providerName}`,
    `ABN: ${invoiceDraft.provider.abn || 'Not entered'}`,
    `Participant: ${participantName}`,
    `NDIS/reference: ${invoiceDraft.participant.ndisNumber || 'Not entered'}`,
    '',
    lines || 'No line items entered.',
    '',
    `Subtotal: ${money(subtotal())}`,
    `GST: ${money(gstTotal())}`,
    `Total: ${money(grandTotal())}`,
  ].join('\n');
}

function invoiceHtml(): string {
  return htmlCoverEmail();
}

function plainTextCoverEmail(): string {
  const invoiceNumber = invoiceDraft.invoiceNumber || '[no invoice number]';
  const participantName = participantDisplayName();
  return [
    'Hello,',
    '',
    'Attached is a draft test invoice for review.',
    '',
    `Invoice number: ${invoiceNumber}`,
    `Participant: ${participantName}`,
    `Provider: ${providerDisplayName()}`,
    `Service date: ${invoiceDraft.serviceDate}`,
    `Total: ${money(grandTotal())}`,
    '',
    'This is a test draft only. It has not been sent to the participant, plan manager, provider, or NDIA. No NDIS claim has been submitted.',
    '',
    `If there are any issues with this invoice draft, please contact ${ACCOUNT_EMAIL}.`,
  ].join('\n');
}

function htmlCoverEmail(): string {
  const invoiceNumber = invoiceDraft.invoiceNumber || '[no invoice number]';
  const participantName = participantDisplayName();
  return `
    <div style="font-family:Arial,sans-serif;line-height:1.5">
      <p>Hello,</p>
      <p>Attached is a draft test invoice for review.</p>
      <table cellpadding="6" cellspacing="0" style="border-collapse:collapse">
        <tbody>
          <tr><td><strong>Invoice number</strong></td><td>${esc(invoiceNumber)}</td></tr>
          <tr><td><strong>Participant</strong></td><td>${esc(participantName)}</td></tr>
          <tr><td><strong>Provider</strong></td><td>${esc(providerDisplayName())}</td></tr>
          <tr><td><strong>Service date</strong></td><td>${esc(invoiceDraft.serviceDate)}</td></tr>
          <tr><td><strong>Total</strong></td><td>${esc(money(grandTotal()))}</td></tr>
        </tbody>
      </table>
      <p>This is a test draft only. It has not been sent to the participant, plan manager, provider, or NDIA. No NDIS claim has been submitted.</p>
      <p>If there are any issues with this invoice draft, please contact ${esc(ACCOUNT_EMAIL)}.</p>
    </div>
  `;
}

function generateInvoicePdf(): string {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  const right = pageWidth - margin;
  const contentWidth = pageWidth - (2 * margin);
  const providerName = providerDisplayName();
  const invoiceNumber = invoiceDraft.invoiceNumber || '[no invoice number]';
  const heading = invoiceDraft.provider.gstRegistered === 'yes' ? 'TAX INVOICE' : 'INVOICE';
  const bodyBottom = pageHeight - 24;
  let y = 16;
  const addTextLines = (lines: string[], x: number, startY: number, lineHeight = 5): number => {
    let currentY = startY;
    for (const line of lines) {
      doc.text(line, x, currentY);
      currentY += lineHeight;
    }
    return currentY;
  };
  const ensureSpace = (height: number): void => {
    if (y + height <= bodyBottom) return;
    doc.addPage();
    y = 18;
  };
  const addWatermarkAndFooters = (): void => {
    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(48);
      doc.setTextColor(220, 220, 220);
      doc.text('DRAFT', pageWidth / 2, pageHeight / 2, { align: 'center', angle: -35 });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(90, 90, 90);
      doc.text(GENERATED_FOOTER_TEXT, margin, pageHeight - 10);
      doc.text('DRAFT -- REVIEW REQUIRED -- No NDIS claim submitted', right, pageHeight - 10, { align: 'right' });
    }
  };

  doc.setTextColor(20, 20, 20);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text(heading, margin, y);
  doc.setFontSize(10);
  const invoiceDetails = [
    { label: 'Invoice number', value: invoiceNumber, boldValue: true },
    { label: 'Invoice date', value: invoiceDraft.invoiceDate || 'Not entered', boldValue: false },
    { label: 'Service date', value: invoiceDraft.serviceDate || 'Not entered', boldValue: false },
    ...(invoiceDraft.dueDate ? [{ label: 'Due date', value: invoiceDraft.dueDate, boldValue: false }] : []),
  ];
  let invoiceY = y;
  for (const detail of invoiceDetails) {
    const labelText = `${detail.label}: `;
    doc.setFont('helvetica', 'normal');
    const labelWidth = doc.getTextWidth(labelText);
    doc.setFont('helvetica', detail.boldValue ? 'bold' : 'normal');
    const valueWidth = doc.getTextWidth(detail.value);
    const startX = right - labelWidth - valueWidth;
    doc.setFont('helvetica', 'normal');
    doc.text(labelText, startX, invoiceY);
    doc.setFont('helvetica', detail.boldValue ? 'bold' : 'normal');
    doc.text(detail.value, startX + labelWidth, invoiceY);
    invoiceY += 5;
  }
  y = Math.max(y + 10, invoiceY) + 8;

  doc.setTextColor(20, 20, 20);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  const billToX = margin + 104;
  const partyY = y;
  doc.text('FROM', margin, partyY);
  doc.text('TO / BILL TO', billToX, y);
  y = partyY + 5;
  doc.setTextColor(20, 20, 20);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(providerName, margin, y);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const providerEndY = addTextLines(providerDetailLines(), margin, y + 5);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text(participantDisplayName(), billToX, y);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const billToEndY = addTextLines(billToDetailLines().slice(1), billToX, y + 5);
  y = Math.max(providerEndY, billToEndY) + 8;

  doc.setDrawColor(190, 190, 190);
  doc.line(margin, y, right, y);
  y += 8;

  const headers = ['Date', 'Code', 'Description', 'Time', 'Qty', 'Unit', 'Rate', 'GST', 'Total'];
  const widths = [18, 24, 44, 20, 14, 14, 18, 14, 16];
  const descriptionWidth = 44;
  const drawHeader = (): void => {
    let x = margin;
    doc.setFillColor(238, 238, 238);
    doc.rect(margin, y - 4, contentWidth, 8, 'F');
    doc.setTextColor(20, 20, 20);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    headers.forEach((header, index) => {
      doc.text(header, x + 1, y);
      x += widths[index] ?? 20;
    });
    y += 6;
    doc.setFont('helvetica', 'normal');
  };
  drawHeader();
  invoiceDraft.lineItems.forEach((item, index) => {
    const description = doc.splitTextToSize(item.supportItemName || 'Not entered', descriptionWidth - 2);
    const rowHeight = Math.max(8, description.length * 4 + 3);
    if (y + rowHeight > bodyBottom) {
      doc.addPage();
      y = 18;
      drawHeader();
    }
    if (index % 2 === 0) {
      doc.setFillColor(248, 248, 248);
      doc.rect(margin, y - 4, contentWidth, rowHeight, 'F');
    } else {
      doc.setFillColor(255, 255, 255);
      doc.rect(margin, y - 4, contentWidth, rowHeight, 'F');
    }
    let x = margin;
    const cells = [
      item.date,
      item.supportItemCode,
      '',
      lineTimeRange(item),
      item.quantity.toFixed(2),
      item.unit,
      money(item.rate),
      money(item.gstComponent),
      money(lineTotal(item)),
    ];
    cells.forEach((cell, cellIndex) => {
      if (cellIndex === 2) {
        doc.text(description, x + 1, y);
      } else {
        doc.text(cell || '-', x + 1, y);
      }
      x += widths[cellIndex] ?? 20;
    });
    y += rowHeight;
  });

  y += 8;
  ensureSpace(54);
  const totalX = 140;
  doc.setTextColor(20, 20, 20);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text('Subtotal', totalX, y);
  doc.text(money(subtotal()), right, y, { align: 'right' });
  y += 6;
  doc.text('GST', totalX, y);
  doc.text(money(gstTotal()), right, y, { align: 'right' });
  y += 7;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Total Due', totalX, y);
  doc.text(money(grandTotal()), right, y, { align: 'right' });
  y += 12;

  ensureSpace(28);
  doc.setDrawColor(150, 150, 150);
  doc.setLineDashPattern([1, 1], 0);
  doc.line(margin, y, right, y);
  doc.setLineDashPattern([], 0);
  y += 7;
  doc.setTextColor(20, 20, 20);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Payment / Pay To', margin, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  y = addTextLines(paymentDetailsLines(), margin, y);

  addWatermarkAndFooters();

  const dataUri = doc.output('datauristring');
  return dataUri.includes(',') ? dataUri.split(',')[1] ?? dataUri : dataUri;
}

function downloadInvoicePdf(): void {
  const pdfBase64 = generateInvoicePdf();
  const link = document.createElement('a');
  link.href = `data:application/pdf;base64,${pdfBase64}`;
  link.download = `invoice-${invoiceDraft.invoiceNumber || 'draft'}.pdf`;
  link.click();
}

function renderPreviewTable(): string {
  return `
    <table class="nid-preview-table">
      <thead><tr><th>Date</th><th>Code</th><th>Description</th><th>Time</th><th>Qty</th><th>Unit</th><th>Rate</th><th>GST</th><th>Total</th></tr></thead>
      <tbody>${invoiceDraft.lineItems.map(item => `
        <tr>
          <td>${esc(item.date)}</td>
          <td>${esc(item.supportItemCode)}</td>
          <td>${esc(item.supportItemName)}</td>
          <td>${esc(lineTimeRange(item))}</td>
          <td>${item.quantity.toFixed(2)}</td>
          <td>${esc(item.unit)}</td>
          <td>${money(item.rate)}</td>
          <td>${money(item.gstComponent)}</td>
          <td>${money(lineTotal(item))}</td>
        </tr>
      `).join('')}</tbody>
    </table>
  `;
}

function renderPreview(): string {
  const heading = invoiceDraft.provider.gstRegistered === 'yes' ? 'TAX INVOICE' : 'INVOICE';
  const invoiceNumber = invoiceDraft.invoiceNumber || 'Not entered';
  const invoiceDetails = [
    `Invoice number: ${invoiceNumber}`,
    `Invoice date: ${invoiceDraft.invoiceDate || 'Not entered'}`,
    `Service date: ${invoiceDraft.serviceDate || 'Not entered'}`,
    invoiceDraft.dueDate ? `Due date: ${invoiceDraft.dueDate}` : '',
  ].filter(line => line.trim().length > 0);
  return `
    <div class="nid-preview">
      <div class="nid-watermark">DRAFT -- REVIEW REQUIRED</div>
      <div class="nid-preview-head">
        <div>
          <h2 class="nid-preview-title">${esc(heading)}</h2>
        </div>
        <div class="nid-preview-meta">
          <div class="nid-preview-block">${invoiceDetails.map((line, index) => index === 0 ? `<strong>${esc(line)}</strong>` : esc(line)).join('\n')}</div>
        </div>
      </div>
      <div class="nid-preview-party-row">
        <section>
          <div class="nid-preview-label">FROM</div>
          <div class="nid-preview-block"><strong>${esc(providerDisplayName())}</strong>
${providerDetailLines().map(esc).join('\n')}</div>
        </section>
        <section>
          <div class="nid-preview-label">TO / BILL TO</div>
          <div class="nid-preview-block"><strong>${esc(participantDisplayName())}</strong>
${billToDetailLines().slice(1).map(esc).join('\n')}</div>
        </section>
      </div>
      ${renderPreviewTable()}
      <div class="nid-totals">
        <div class="nid-total-row"><span>Subtotal</span><strong>${money(subtotal())}</strong></div>
        <div class="nid-total-row"><span>GST</span><strong>${money(gstTotal())}</strong></div>
        <div class="nid-total-row"><span>Total Due</span><strong>${money(grandTotal())}</strong></div>
      </div>
      <section class="nid-preview-section nid-preview-payto">
        <h3 class="nid-preview-section-title">Payment / Pay To</h3>
        <div class="nid-preview-block">${paymentDetailsLines().map(esc).join('\n')}</div>
      </section>
      <footer class="nid-preview-footer">
        <span>${esc(GENERATED_FOOTER_TEXT)}</span>
        <span>DRAFT -- REVIEW REQUIRED -- No NDIS claim submitted</span>
      </footer>
    </div>
  `;
}

function renderPreviewTab(): string {
  snapshotBillingIdentity();
  saveInvoice();
  return `
    <section class="nid-card">
      <h2>Preview & Send</h2>
      <div class="nid-warning">This is an NDIS-aware draft invoice. Confirm against current NDIS Support Catalogue and Pricing Arrangements before use.</div>
      <div class="nid-warning">Budgets show available plan funding. Support item codes are for charging. Catalogue matches are suggestions only and must be reviewed before use.</div>
      <div class="nid-warning">No claim submitted. Manual review required before use.</div>
      <div class="nid-actions">
        <button class="nid-button" id="nid-print" type="button">Print</button>
        <button class="nid-button nid-button-secondary" id="nid-download-pdf" type="button">Download PDF</button>
        <button class="nid-button nid-button-secondary" id="nid-copy-summary" type="button">Copy invoice summary text</button>
        <button class="nid-button" id="nid-send-test" type="button" ${sendingEmail ? 'disabled' : ''}>${sendingEmail ? 'Generating PDF and sending' : 'Send test invoice to Mitchell'}</button>
        <button class="nid-button" id="nid-send-provider-review" type="button" ${sendingReviewEmail ? 'disabled' : ''}>${sendingReviewEmail ? 'Saving review draft and sending' : 'Send Provider Review Test to Mitchell'}</button>
        <button class="nid-button nid-button-secondary" type="button" disabled title="Coming later">Send to Participant Billing<span class="nid-disabled-note">Coming later</span></button>
        <button class="nid-button nid-button-secondary" type="button" disabled title="Coming later">Send to Provider<span class="nid-disabled-note">Coming later</span></button>
      </div>
      ${emailResult ? `<div class="nid-result nid-result-${emailResult.status}">${esc(emailResult.message)}<br>${esc(emailResult.timestamp)}</div>` : ''}
      <details class="nid-future">
        <summary>Future automation</summary>
        <ul class="nid-future-list">
          <li>Provider-created invoices from completed jobs</li>
          <li>Participant/job approval workflow</li>
          <li>Pre-approved support codes/rates under agreement</li>
          <li>Auto-validation for within-agreement items</li>
          <li>Manual review for out-of-agreement items</li>
          <li>Participant/plan manager send with approval</li>
        </ul>
      </details>
    </section>
    <section class="nid-card nid-printable">
      ${renderPreview()}
    </section>
  `;
}

function renderCatalogueModal(): string {
  if (!catalogueModalOpen) return '';
  const catalogue = loadCatalogue();
  const allMatches = searchCatalogueItems(catalogue, catalogueSearch);
  const matches = searchCatalogueItems(catalogue, catalogueSearch, 50);
  const groupedMatches = groupCatalogueByCategory(matches);
  const groupedCatalogue = groupCatalogueByCategory(catalogue);
  const resultText = allMatches.length === 0 ? 'No results found' : `${allMatches.length} results`;
  return `
    <div class="nid-modal-backdrop" role="dialog" aria-modal="true">
      <div class="nid-modal">
        <div class="nid-modal-head">
          <div>
            <h2 class="nid-modal-title">Add from NDIS catalogue</h2>
            <div class="nid-help">Catalogue matches are suggestions only and must be reviewed before use.</div>
          </div>
          <button class="nid-button nid-button-secondary" id="nid-close-catalogue" type="button">Close</button>
        </div>
        ${catalogue.length === 0 ? '<div class="nid-warning">No catalogue loaded. Upload the official NDIS Support Catalogue XLSX on the NDIS Extraction page, then return here.</div>' : `
          <div class="nid-modal-tabs">
            <button class="nid-modal-tab ${catalogueModalMode === 'search' ? 'nid-modal-tab-active' : ''}" type="button" data-catalogue-mode="search">Search</button>
            <button class="nid-modal-tab ${catalogueModalMode === 'browse' ? 'nid-modal-tab-active' : ''}" type="button" data-catalogue-mode="browse">Browse by category</button>
          </div>
          ${catalogueModalMode === 'search' ? `
            <input class="nid-input nid-catalogue-search" id="nid-catalogue-search" type="search" value="${esc(catalogueSearch)}" placeholder="Search code, name, category, or registration group" autofocus>
            <div class="nid-results-count">${esc(resultText)}</div>
            <div class="nid-cat-list">${renderGroupedCatalogueRows(groupedMatches)}</div>
          ` : `
            <div class="nid-results-count">${catalogue.length} catalogue items across ${groupedCatalogue.length} categories</div>
            <div class="nid-cat-list">${groupedCatalogue.map(renderCatalogueCategory).join('')}</div>
          `}
        `}
      </div>
    </div>
  `;
}

function renderGroupedCatalogueRows(groups: Array<{ category: string; items: CatalogueItem[] }>): string {
  return groups.map(group => `
    <div class="nid-cat-group">${esc(group.category)} (${group.items.length})</div>
    ${group.items.map(renderCatalogueRow).join('')}
  `).join('');
}

function renderCatalogueCategory(group: { category: string; items: CatalogueItem[] }): string {
  const expanded = expandedCatalogueCategories.has(group.category);
  return `
    <section class="nid-cat-category">
      <button class="nid-cat-category-head" type="button" data-toggle-category="${esc(group.category)}">
        <span>${esc(group.category)}</span>
        <span>${group.items.length} items</span>
      </button>
      ${expanded ? `<div class="nid-cat-category-items">${group.items.map(renderCatalogueRow).join('')}</div>` : ''}
    </section>
  `;
}

function renderCatalogueRow(item: CatalogueItem): string {
  const unit = normaliseUnit(item.unit);
  return `
    <div class="nid-cat-row">
      <div class="nid-cat-code">${esc(item.supportItemNumber)}</div>
      <div class="nid-cat-name">${esc(item.supportItemName)}<div class="nid-help">${esc(item.supportCategory)} ${esc(item.registrationGroup)}</div></div>
      <div class="nid-cat-price">${money(parseRate(item.priceLimit))}/${esc(item.unit || unit)}</div>
      <button class="nid-button nid-button-secondary" type="button" data-select-catalogue="${esc(item.supportItemNumber)}">Select</button>
    </div>
  `;
}

function normaliseUnit(unit: string): InvoiceLineItem['unit'] {
  const lower = unit.toLowerCase();
  if (lower.includes('km')) return 'Km';
  if (lower.includes('day')) return 'Day';
  if (lower.includes('each')) return 'Each';
  return 'Hour';
}

function loadCatalogue(): CatalogueItem[] {
  return readCatalogueFromStorage(CATALOGUE_STORAGE_KEY);
}

function renderPage(page: HTMLElement): void {
  syncCalculatedDuration();
  saveInvoice();
  const tabContent = activeTab === 'session'
    ? renderSessionTab()
    : activeTab === 'details'
      ? renderDetailsTab()
      : activeTab === 'lines'
        ? renderLineItemsTab()
        : activeTab === 'validation'
          ? renderValidationTab()
          : renderPreviewTab();
  page.innerHTML = `
    <main class="nid-shell">
      <div class="nid-topbar">
        <header class="nid-header">
          <div class="nid-brand">Draft invoice tool</div>
          <h1>Support Invoice Generator</h1>
          <p>Manual NDIS-aware support invoice draft with review checks and staged test email.</p>
        </header>
        <div class="nid-badges">
          <span class="nid-badge nid-badge-warning">Auth-gated</span>
          <span class="nid-badge nid-badge-warning">Draft</span>
          <span class="nid-badge nid-badge-warning">Review required</span>
        </div>
      </div>
      <div class="nid-warning">DRAFT ONLY. No NDIS claim submitted. No invoice sent unless the Mitchell-only test email is confirmed.</div>
      ${renderTabs()}
      ${tabContent}
      ${renderCatalogueModal()}
    </main>
  `;
  wireEvents(page);
}

function updateField(field: string, value: string): void {
  switch (field) {
    case 'invoiceNumber': invoiceDraft.invoiceNumber = value; break;
    case 'invoiceDate': invoiceDraft.invoiceDate = value; break;
    case 'serviceDate': invoiceDraft.serviceDate = value; invoiceDraft.session.serviceDate = value; break;
    case 'dueDate': invoiceDraft.dueDate = value; break;
    case 'referenceNote': invoiceDraft.referenceNote = value; break;
    case 'participant.name': invoiceDraft.participant.name = value; invoiceDraft.session.participantName = value; break;
    case 'participant.ndisNumber': invoiceDraft.participant.ndisNumber = value; break;
    case 'participant.billingType': invoiceDraft.participant.billingType = value as ParticipantProfileDraft['billingType']; break;
    case 'participant.planManagerName': invoiceDraft.participant.planManagerName = value; break;
    case 'participant.planManagerEmail': invoiceDraft.participant.planManagerEmail = value; break;
    case 'participant.phone': invoiceDraft.participant.phone = value; break;
    case 'participant.address': invoiceDraft.participant.address = value; break;
    case 'participant.invoiceNotes': invoiceDraft.participant.invoiceNotes = value; break;
    case 'participant.preferredInvoiceRecipient': invoiceDraft.participant.preferredInvoiceRecipient = value; break;
    case 'provider.providerType': invoiceDraft.provider.providerType = value as ProviderProfileDraft['providerType']; break;
    case 'provider.businessName': invoiceDraft.provider.businessName = value; invoiceDraft.session.providerName = value; break;
    case 'provider.supportWorkerName': invoiceDraft.provider.supportWorkerName = value; break;
    case 'provider.abn': invoiceDraft.provider.abn = value; break;
    case 'provider.invoiceEmail': invoiceDraft.provider.invoiceEmail = value; break;
    case 'provider.phone': invoiceDraft.provider.phone = value; break;
    case 'provider.address': invoiceDraft.provider.address = value; break;
    case 'provider.serviceTypes': invoiceDraft.provider.serviceTypes = value; break;
    case 'provider.defaultHourlyRate': invoiceDraft.provider.defaultHourlyRate = value; break;
    case 'provider.defaultKmRate': invoiceDraft.provider.defaultKmRate = value; break;
    case 'provider.gstRegistered': invoiceDraft.provider.gstRegistered = value as ProviderProfileDraft['gstRegistered']; break;
    case 'provider.paymentTerms': invoiceDraft.provider.paymentTerms = value; break;
    case 'provider.invoiceFooter': invoiceDraft.provider.invoiceFooter = value; break;
    case 'provider.paymentAccountName': invoiceDraft.provider.paymentAccountName = value; break;
    case 'provider.paymentBSB': invoiceDraft.provider.paymentBSB = value; break;
    case 'provider.paymentAccountNumber': invoiceDraft.provider.paymentAccountNumber = value; break;
    case 'provider.paymentReferencePreference': invoiceDraft.provider.paymentReferencePreference = value as ProviderProfileDraft['paymentReferencePreference']; break;
    case 'provider.paymentReferenceCustom': invoiceDraft.provider.paymentReferenceCustom = value; break;
    case 'provider.paymentNotes': invoiceDraft.provider.paymentNotes = value; break;
    case 'provider.paymentDetailsPlaceholder': invoiceDraft.provider.paymentDetailsPlaceholder = value; break;
    case 'session.participantName': invoiceDraft.session.participantName = value; break;
    case 'session.providerName': invoiceDraft.session.providerName = value; break;
    case 'session.serviceDate': invoiceDraft.session.serviceDate = value; invoiceDraft.serviceDate = value; break;
    case 'session.startTime': invoiceDraft.session.startTime = value; break;
    case 'session.endTime': invoiceDraft.session.endTime = value; break;
    case 'session.breakMinutes': invoiceDraft.session.breakMinutes = numberValue(value); break;
    case 'session.serviceMode': invoiceDraft.session.serviceMode = value as SupportSessionDraft['serviceMode']; break;
    case 'session.location': invoiceDraft.session.location = value; break;
    case 'session.kmTravelled': invoiceDraft.session.kmTravelled = numberValue(value); break;
    case 'session.kmRate': invoiceDraft.session.kmRate = numberValue(value); break;
    case 'session.supportNotes': invoiceDraft.session.supportNotes = value; break;
    default: break;
  }
}

function updateLine(item: InvoiceLineItem, field: keyof InvoiceLineItem, value: string): void {
  switch (field) {
    case 'supportItemCode':
      item.supportItemCode = value;
      if (item.source !== 'participant-approved' || !isApprovedParticipantCode(value)) item.source = 'manual-needs-review';
      break;
    case 'supportItemName': item.supportItemName = value; break;
    case 'date': item.date = value; break;
    case 'startTime': item.startTime = value; break;
    case 'endTime': item.endTime = value; break;
    case 'duration': item.duration = numberValue(value); break;
    case 'unit': item.unit = value as InvoiceLineItem['unit']; break;
    case 'quantity': item.quantity = numberValue(value); break;
    case 'rate': item.rate = numberValue(value); break;
    case 'gstComponent': item.gstComponent = numberValue(value); break;
    case 'claimType': item.claimType = value; break;
    case 'reviewStatus': item.reviewStatus = value as InvoiceLineItem['reviewStatus']; break;
    case 'id': break;
    case 'catalogueRef': break;
    case 'source': break;
  }
  if (field === 'startTime' || field === 'endTime') {
    item.duration = calculateLineDuration(item);
    if (item.unit === 'Hour' && item.duration > 0) item.quantity = item.duration;
  }
}

function wireEvents(page: HTMLElement): void {
  page.querySelectorAll<HTMLButtonElement>('[data-tab]').forEach(button => {
    button.addEventListener('click', () => {
      activeTab = button.dataset['tab'] as InvoiceTab;
      renderPage(page);
    });
  });
  page.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('[data-field]').forEach(el => {
    const handle = (): void => {
      const field = el.dataset['field'];
      if (!field) return;
      updateField(field, el.value);
      syncCalculatedDuration();
      saveInvoice();
    };
    el.addEventListener('input', handle);
    el.addEventListener('change', () => {
      handle();
      renderPage(page);
    });
  });
  page.querySelector<HTMLButtonElement>('#nid-load-participant')?.addEventListener('click', () => {
    loadParticipantProfile();
    renderPage(page);
  });
  page.querySelector<HTMLButtonElement>('#nid-load-provider')?.addEventListener('click', () => {
    loadProviderProfile();
    renderPage(page);
  });
  page.querySelector<HTMLButtonElement>('#nid-suggest-next')?.addEventListener('click', () => {
    invoiceDraft.invoiceNumber = `INV-${new Date().getFullYear()}-001`;
    renderPage(page);
  });
  page.querySelector<HTMLButtonElement>('#nid-add-blank-line')?.addEventListener('click', () => {
    invoiceDraft.lineItems.push(defaultLineItem());
    renderPage(page);
  });
  page.querySelector<HTMLButtonElement>('#nid-add-from-catalogue')?.addEventListener('click', () => {
    targetLineId = null;
    catalogueSearch = '';
    catalogueModalMode = 'search';
    catalogueModalOpen = true;
    renderPage(page);
  });
  page.querySelector<HTMLButtonElement>('#nid-duplicate-last-line')?.addEventListener('click', () => {
    const lastLine = invoiceDraft.lineItems.at(-1);
    if (!lastLine) return;
    invoiceDraft.lineItems.push(duplicateLineItem(lastLine));
    renderPage(page);
  });
  page.querySelectorAll<HTMLButtonElement>('[data-use-participant-code]').forEach(button => {
    button.addEventListener('click', () => {
      const supportItemCode = button.dataset['useParticipantCode'] ?? '';
      const code = approvedParticipantCodes().find(item => item.supportItemCode === supportItemCode);
      if (!code) return;
      const line = defaultLineItem();
      applyParticipantCode(line, code);
      invoiceDraft.lineItems.push(line);
      renderPage(page);
    });
  });
  page.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-line-field]').forEach(el => {
    const handle = (): void => {
      const card = el.closest<HTMLElement>('[data-line-id]');
      const id = card?.dataset['lineId'];
      const field = el.dataset['lineField'] as keyof InvoiceLineItem | undefined;
      const item = invoiceDraft.lineItems.find(line => line.id === id);
      if (!item || !field) return;
      updateLine(item, field, el.value);
      saveInvoice();
    };
    el.addEventListener('input', handle);
    el.addEventListener('change', () => {
      handle();
      renderPage(page);
    });
  });
  page.querySelectorAll<HTMLButtonElement>('[data-remove-line]').forEach(button => {
    button.addEventListener('click', () => {
      const id = button.dataset['removeLine'];
      invoiceDraft.lineItems = invoiceDraft.lineItems.filter(item => item.id !== id);
      renderPage(page);
    });
  });
  page.querySelectorAll<HTMLButtonElement>('[data-catalogue-line]').forEach(button => {
    button.addEventListener('click', () => {
      targetLineId = button.dataset['catalogueLine'] ?? null;
      catalogueSearch = '';
      catalogueModalMode = 'search';
      catalogueModalOpen = true;
      renderPage(page);
    });
  });
  page.querySelector<HTMLButtonElement>('#nid-close-catalogue')?.addEventListener('click', () => {
    catalogueModalOpen = false;
    renderPage(page);
  });
  page.querySelectorAll<HTMLButtonElement>('[data-catalogue-mode]').forEach(button => {
    button.addEventListener('click', () => {
      catalogueModalMode = button.dataset['catalogueMode'] === 'browse' ? 'browse' : 'search';
      renderPage(page);
    });
  });
  page.querySelector<HTMLInputElement>('#nid-catalogue-search')?.addEventListener('input', event => {
    catalogueSearch = (event.target as HTMLInputElement).value;
    renderPage(page);
  });
  page.querySelector<HTMLInputElement>('#nid-catalogue-search')?.focus();
  page.querySelectorAll<HTMLButtonElement>('[data-toggle-category]').forEach(button => {
    button.addEventListener('click', () => {
      const category = button.dataset['toggleCategory'];
      if (!category) return;
      if (expandedCatalogueCategories.has(category)) {
        expandedCatalogueCategories.delete(category);
      } else {
        expandedCatalogueCategories.add(category);
      }
      renderPage(page);
    });
  });
  page.querySelectorAll<HTMLButtonElement>('[data-select-catalogue]').forEach(button => {
    button.addEventListener('click', () => {
      const supportItemNumber = button.dataset['selectCatalogue'] ?? '';
      const catalogueItem = loadCatalogue().find(item => item.supportItemNumber === supportItemNumber);
      if (!catalogueItem) return;
      const item = invoiceDraft.lineItems.find(line => line.id === targetLineId) ?? defaultLineItem();
      applyCatalogueItem(item, catalogueItem);
      if (!targetLineId) invoiceDraft.lineItems.push(item);
      catalogueModalOpen = false;
      renderPage(page);
    });
  });
  page.querySelector<HTMLButtonElement>('#nid-print')?.addEventListener('click', () => window.print());
  page.querySelector<HTMLButtonElement>('#nid-download-pdf')?.addEventListener('click', () => {
    downloadInvoicePdf();
    emailResult = { status: 'success', message: 'PDF downloaded.', timestamp: new Date().toLocaleString('en-AU') };
    renderPage(page);
  });
  page.querySelector<HTMLButtonElement>('#nid-copy-summary')?.addEventListener('click', () => {
    void navigator.clipboard.writeText(invoicePlainText()).then(() => {
      emailResult = { status: 'success', message: 'Invoice summary copied.', timestamp: new Date().toLocaleString('en-AU') };
      renderPage(page);
    }).catch(() => {
      emailResult = { status: 'error', message: 'Copy failed.', timestamp: new Date().toLocaleString('en-AU') };
      renderPage(page);
    });
  });
  page.querySelector<HTMLButtonElement>('#nid-send-test')?.addEventListener('click', () => {
    if (!confirm('This sends a test draft only to Mitchell (tuckett.mitchell@gmail.com). A PDF invoice will be attached. It does not send to the participant, plan manager, provider, or NDIA.')) return;
    void sendTestEmail(page);
  });
  page.querySelector<HTMLButtonElement>('#nid-send-provider-review')?.addEventListener('click', () => {
    if (!confirm('This sends a provider review test link only to Mitchell (tuckett.mitchell@gmail.com). It does not send an invoice to the participant, plan manager, provider, or NDIA.')) return;
    void sendProviderReviewTest(page);
  });
}

async function sendTestEmail(page: HTMLElement): Promise<void> {
  const invoiceNumber = invoiceDraft.invoiceNumber || '[no invoice number]';
  const participantName = invoiceDraft.participant.name || invoiceDraft.session.participantName || 'Participant not entered';
  sendingEmail = true;
  renderPage(page);
  try {
    const pdfBase64 = generateInvoicePdf();
    const res = await fetch(`${API_BASE}/api/email/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        from: ACCOUNT_EMAIL,
        to: 'tuckett.mitchell@gmail.com',
        subject: `DRAFT TEST Invoice ${invoiceNumber} for ${participantName}`,
        body: plainTextCoverEmail(),
        body_html: invoiceHtml(),
        attachments: [{
          filename: `invoice-${invoiceDraft.invoiceNumber || 'draft'}.pdf`,
          content: pdfBase64,
        }],
      }),
    });
    if (!res.ok) throw new Error(`Email API returned ${res.status}`);
    emailResult = { status: 'success', message: 'Test draft email with PDF attachment sent to Mitchell.', timestamp: new Date().toLocaleString('en-AU') };
  } catch (err) {
    emailResult = { status: 'error', message: err instanceof Error ? err.message : 'Email send failed.', timestamp: new Date().toLocaleString('en-AU') };
  }
  sendingEmail = false;
  renderPage(page);
}

function providerReviewLink(): string {
  return `${window.location.origin}/people/test/support-v2-draft/provider-invoice-review`;
}

function saveProviderReviewDraft(): void {
  snapshotBillingIdentity();
  localStorage.setItem(INVOICE_REVIEW_STORAGE_KEY, JSON.stringify(invoiceDraft));
  saveInvoice();
}

function providerReviewEmailBody(): string {
  const invoiceNumber = invoiceDraft.invoiceNumber || '[no invoice number]';
  const participantName = participantDisplayName();
  const link = providerReviewLink();
  return [
    'This is a provider invoice review test.',
    '',
    'Open the link below to review the invoice as a support worker/provider.',
    '',
    link,
    '',
    `Invoice number: ${invoiceNumber}`,
    `Participant: ${participantName}`,
    `Total: ${money(grandTotal())}`,
    '',
    'No invoice has been sent to participant, plan manager, provider, or NDIA.',
    '',
    `If there are any issues, contact ${ACCOUNT_EMAIL}.`,
  ].join('\n');
}

async function sendProviderReviewTest(page: HTMLElement): Promise<void> {
  const invoiceNumber = invoiceDraft.invoiceNumber || '[no invoice number]';
  const participantName = participantDisplayName();
  sendingReviewEmail = true;
  saveProviderReviewDraft();
  renderPage(page);
  try {
    const res = await fetch(`${API_BASE}/api/email/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        from: ACCOUNT_EMAIL,
        to: 'tuckett.mitchell@gmail.com',
        subject: `PROVIDER REVIEW TEST — Invoice ${invoiceNumber} for ${participantName}`,
        body: providerReviewEmailBody(),
      }),
    });
    if (!res.ok) throw new Error(`Email API returned ${res.status}`);
    emailResult = { status: 'success', message: 'Provider review test link sent to Mitchell.', timestamp: new Date().toLocaleString('en-AU') };
    showToast('Provider review test link sent to Mitchell.', 'ok');
  } catch (err) {
    emailResult = { status: 'error', message: err instanceof Error ? err.message : 'Provider review send failed.', timestamp: new Date().toLocaleString('en-AU') };
    showToast(emailResult.message, 'err');
  }
  sendingReviewEmail = false;
  renderPage(page);
}

export function isNdisInvoiceDraftPath(): boolean {
  return window.location.pathname.includes('/ndis-invoice-draft');
}

export function renderNdisInvoiceDraft(): void {
  invoiceDraft = readInvoice();
  applyDemoDefaults();
  if (invoiceDraft.lineItems.length === 0) invoiceDraft.lineItems.push(defaultLineItem());
  activeTab = 'session';
  catalogueModalOpen = false;
  catalogueModalMode = 'search';
  expandedCatalogueCategories = new Set<string>();
  emailResult = null;
  sendingEmail = false;
  sendingReviewEmail = false;
  renderPage(shell('Support Invoice Generator - Hub People'));
}
